import { c } from "./colors";

/**
 * Themes incur's built-in help / error / CTA text so the whole CLI looks
 * consistent with our hand-rendered surfaces (root help, quickstart, tables).
 *
 * incur exposes a `renderer` hook only for command *data*, not for help or
 * errors, so we post-process the text it writes to stdout instead. This runs
 * only in human TTY mode (see `shouldColorize`) — structured output
 * (`--json`, `--format`, piped/agent) is passed through untouched, so machine
 * consumers never see ANSI codes.
 */

const SECTION_HEADERS = new Set([
	"Usage",
	"Arguments",
	"Options",
	"Examples",
	"Environment Variables",
	"Global Options",
	"Integrations",
	"Commands",
	"Aliases",
]);

const FORMAT_FLAGS = ["--json", "--format", "--full-output", "--schema", "--llms", "--llms-full", "--mcp", "--token-count", "--token-limit", "--token-offset"];

/** True when argv carries any machine-output flag (`--json`, `--format <fmt>`, `--format=<fmt>`, `--schema`, …). */
export function hasStructuredOutputFlag(argv: string[]): boolean {
	return argv.some((a) => FORMAT_FLAGS.includes(a) || a.startsWith("--format="));
}

/** Color only when writing for a human: a TTY, with no structured-output flag. */
export function shouldColorize(argv: string[]): boolean {
	if (!process.stdout.isTTY) return false;
	return !hasStructuredOutputFlag(argv);
}

/** Highlights a flag token and any `<placeholder>` inside an option/flag row. */
function paintFlag(flag: string): string {
	return flag.replace(/(<[^>]+>)/g, (m) => c.muted(m)).replace(/(--[\w-]+|-[\w])/g, (m) => c.flag(m));
}

/** An indented command line (`  whop products create  # desc`) — in help examples or a CTA. */
const COMMAND_ROW = /^(\s+)(whop\s+\S.*?)(\s{2,}#\s.*)?$/;

/** Underlines any URL so links (checkout pages, dashboards) stand out. */
function highlightUrls(text: string): string {
	return text.replace(/https?:\/\/[^\s)]+/g, (m) => c.link(m));
}

function colorizeLine(
	line: string,
	section: string | null,
	nextLine: string | undefined,
): { line: string; section: string | null } {
	// Error header, e.g. "Error: msg" or "Error (HTTP_403): msg".
	const errMatch = line.match(/^(Error(?: \([^)]+\))?:)(.*)$/);
	if (errMatch) return { line: c.error(errMatch[1]) + highlightUrls(errMatch[2]), section };
	if (line.startsWith("Run `")) return { line: c.heading(line), section };

	// Section header, e.g. "Options:" or "Usage: whop products create".
	const header = line.match(/^([A-Z][A-Za-z ]+):(.*)$/);
	if (header && SECTION_HEADERS.has(header[1])) {
		const rest = header[2].replace(/(whop[\w\s-]*)/g, (m) => c.command(m));
		return { line: c.heading(`${header[1]}:`) + rest, section: header[1] };
	}

	// A `whop …` command line anywhere (help examples and CTAs alike).
	const cmd = line.match(COMMAND_ROW);
	if (cmd) {
		return { line: `${cmd[1]}${c.command(cmd[2])}${cmd[3] ? c.description(cmd[3]) : ""}`, section };
	}

	// A header-like line ("Suggested command:", a CTA description) immediately
	// followed by a command — bold it for emphasis.
	if (line.trim().endsWith(":") && nextLine !== undefined && COMMAND_ROW.test(nextLine)) {
		return { line: c.heading(line), section };
	}

	if (line.trim() === "") return { line, section: null };

	// Rows inside a known section.
	if (section === "Options" || section === "Global Options" || section === "Environment Variables") {
		const m = line.match(/^(\s+)(\S.*?)(\s{2,})(.*)$/);
		if (m) return { line: `${m[1]}${paintFlag(m[2])}${m[3]}${c.description(m[4])}`, section };
	}
	if (section === "Commands" || section === "Integrations" || section === "Aliases") {
		const m = line.match(/^(\s+)(\S+)(\s{2,})(.*)$/);
		if (m) return { line: `${m[1]}${c.command(m[2])}${m[3]}${c.description(m[4])}`, section };
	}
	if (section === "Arguments") {
		const m = line.match(/^(\s+)(\S+)(\s{2,})(.*)$/);
		if (m) return { line: `${m[1]}${c.id(m[2])}${m[3]}${c.description(m[4])}`, section };
	}

	// Plain text (messages, CTA descriptions): make any link stand out.
	return { line: highlightUrls(line), section };
}

/** Themes a chunk of incur help/error/CTA text. Safe to call per write. */
export function colorizeOutput(text: string): string {
	let section: string | null = null;
	const lines = text.split("\n");
	return lines
		.map((line, i) => {
			const result = colorizeLine(line, section, lines[i + 1]);
			section = result.section;
			return result.line;
		})
		.join("\n");
}
